import moment from 'moment';
import { useCallback, useState } from 'react';
import { Box, Column, Divider, InputSelect, Row, Text } from '~/commonComponents';
import { AddMoreOnsiteInterviewButton } from '~/components/PermanentJobsV2/components';
import { JobPostingStatus } from '~/domain/PermanentJobs';
import { JobPostingStatusOptions } from './Constants';
import { SuspendedBanner } from './SuspendedBanner';
import { WarningSolidIcon } from '~/images/icons';
import { Colors } from '~/themes/colors';
import { JobStatusInfo } from './JobStatusInfo';
import { ClosedBanner } from './ClosedBanner';

interface IJobOverviewHeaderProps {
  closedAt?: Date;
  hasOnsiteInterviews: boolean;
  id: string;
  officeName: string;
  postedAt?: Date;
  professional: string;
  status: JobPostingStatus;
  suspendedAt?: Date;
  title: string;
  onCloseJob?(): void;
}

export const JobOverviewHeader = ({
  closedAt,
  hasOnsiteInterviews,
  id,
  officeName,
  postedAt,
  professional,
  status,
  suspendedAt,
  title,
  onCloseJob,
}: IJobOverviewHeaderProps) => {
  const [jobStatus, setJobStatus] = useState<JobPostingStatus>(status);

  const isPessimisticActive = status === JobPostingStatus.Active;
  const isPessimisticClosed = status === JobPostingStatus.Closed;
  const isPessimisticSuspended = status === JobPostingStatus.Suspended;

  const isClosed = isPessimisticClosed && closedAt;
  const isSuspended = isPessimisticSuspended && suspendedAt;
  const showCloseOrSuspendDate = isClosed || isSuspended;

  const handleJobStatusChanges = useCallback(
    (newStatus: string) => {
      const status = newStatus as JobPostingStatus;

      if (status === JobPostingStatus.Closed) {
        onCloseJob?.();

        return;
      }

      // Currently, only "Closed" status is supported, not possible to update to other statuses
      setJobStatus(status);
    },
    [onCloseJob],
  );

  return (
    <Column marginInline={115} marginTop={32}>
      <Row justifyContent="space-between">
        <Column>
          <Row alignItems="center" gap={6}>
            <Text bold fontSize={20}>
              {title}
            </Text>
            {isPessimisticSuspended && <WarningSolidIcon color={Colors.warning_500} size={24} />}
          </Row>
          <Text>
            <Text bold>Professional Type</Text>: {professional}
          </Text>
          <Text>
            <Text bold>Office</Text>: {officeName}
          </Text>
          <Row alignItems="center" gap={14}>
            <Text>
              <Text bold>Posted on</Text>: {postedAt ? moment(postedAt).format('MMM DD, YYYY') : ''}
            </Text>
            {showCloseOrSuspendDate && (
              <>
                <Divider isRounded height="100%" marginVertical={0} width={2} />
                {isClosed && (
                  <Text>
                    <Text bold>Closed on</Text>: {moment(closedAt).format('MMM DD, YYYY')}
                  </Text>
                )}
                {isSuspended && (
                  <Text>
                    <Text bold>Suspended on</Text>: {moment(suspendedAt).format('MMM DD, YYYY')}
                  </Text>
                )}
              </>
            )}
          </Row>
        </Column>
        <Column alignItems="flex-end" justifyContent="space-between">
          {isPessimisticActive ? (
            <Row alignItems="center" gap={6}>
              <Text bold fontSize={18} textWrap="nowrap">
                Job Status<Text>:</Text>
              </Text>
              <Box width={180}>
                <InputSelect
                  isSelectVisible={false}
                  options={JobPostingStatusOptions}
                  value={jobStatus}
                  setValue={handleJobStatusChanges}
                />
              </Box>
            </Row>
          ) : (
            <JobStatusInfo jobStatus={status} />
          )}
          {isPessimisticActive && <AddMoreOnsiteInterviewButton jobPostingId={id} variant="lite" />}
        </Column>
      </Row>

      <ClosedBanner hasOnsiteInterviews={hasOnsiteInterviews} jobStatus={status} />
      <SuspendedBanner jobStatus={status} />

      <Box paddingInline={36} marginBottom={6}>
        <Divider marginVertical={22} />
      </Box>
    </Column>
  );
};
